import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const BASE = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#000", text: "#f4f7fb", dim: "#8b96a5", faint: "#3a4149", green: "#00e676", yellow: "#ffd54f", red: "#ff5252", blue: "#54b6ff", purple: "#c792ea" };

function recColor(r) {
  if (r == null) return C.dim;
  return r >= 67 ? C.green : r >= 34 ? C.yellow : C.red;
}

const PRIO = { high: C.red, medium: C.yellow, low: C.dim };
const NUM = { fontVariantNumeric: "tabular-nums" };
const ROTATE_MS = 15000;

const CSS = `
@keyframes glassFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes glassTimer { from { width: 0%; } to { width: 100%; } }
@keyframes navPulse { 0% { transform: scale(1); opacity: .9; } 100% { transform: scale(1.18); opacity: 0; } }
@keyframes radarSweep { to { transform: rotate(360deg); } }
@keyframes micPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.12); opacity: .7; } }
`;

function speakRu(text) {
  if (!window.speechSynthesis) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "ru-RU";
    u.rate = 1.05;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {}
}

const Label = ({ color, children }) => (
  <div style={{ fontSize: 13, letterSpacing: 2.5, color, fontWeight: 800, marginBottom: 8 }}>{children}</div>
);

const Bar = ({ pct, color }) => (
  <div style={{ height: 7, borderRadius: 4, background: "#1c2025", overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: color, borderRadius: 4 }} />
  </div>
);

const StatRow = ({ label, value, color }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <span style={{ fontSize: 15, color: C.dim, letterSpacing: 1.5 }}>{label}</span>
    <span style={{ fontSize: 32, fontWeight: 800, color, ...NUM }}>{value}</span>
  </div>
);

const Ring = ({ value, color, caption }) => {
  const pct = value != null ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <div style={{ width: 148, height: 148, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  background: `conic-gradient(${color} ${pct * 3.6}deg, #23272d 0deg)` }}>
      <div style={{ width: 116, height: 116, borderRadius: "50%", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 44, fontWeight: 900, color, lineHeight: 1, ...NUM }} data-testid="glass-recovery">
          {value != null ? Math.round(value) : "—"}<span style={{ fontSize: 20 }}>%</span>
        </span>
        <span style={{ fontSize: 11, letterSpacing: 1.5, color: C.dim, marginTop: 3 }}>{caption}</span>
      </div>
    </div>
  );
};

const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const row = { display: "flex", alignItems: "center", gap: 10 };

function TodayScreen({ data, glassKey }) {
  const habitPct = data.habits.total ? Math.round((data.habits.done / data.habits.total) * 100) : 0;
  const w = data.weather;
  const [flashing, setFlashing] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    const onKey = async (e) => {
      if (e.key !== "Enter" || busyRef.current) return;
      busyRef.current = true;
      setFlashing(true);
      try {
        const now = new Date();
        const r = await fetch(`${BASE}/api/glass/flash?key=${encodeURIComponent(glassKey)}&hh=${now.getHours()}&mm=${now.getMinutes()}`);
        const d = await r.json();
        if (d.text) speakRu(d.text);
      } catch {}
      setTimeout(() => { busyRef.current = false; setFlashing(false); }, 4000);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); window.speechSynthesis?.cancel(); };
  }, [glassKey]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-today">
      {flashing && (
        <div style={{ position: "absolute", top: 10, right: 16, fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.green }} data-testid="glass-flash-indicator">
          ▶ ФЛЕШ-БРИФ
        </div>
      )}      {w && (
        <div style={{ ...row, gap: 8, marginBottom: 8, fontSize: 17 }} data-testid="glass-today-weather">
          <span style={{ fontSize: 22, lineHeight: 1 }}>{w.glyph}</span>
          <span style={{ fontWeight: 800, ...NUM }}>{w.temp > 0 ? `+${w.temp}` : w.temp}°</span>
          <span style={{ color: C.dim }}>{w.text}</span>
          {w.city && <span style={{ color: C.blue, marginLeft: "auto", fontWeight: 700, ...ellipsis, maxWidth: 220 }}>◈ {w.city}</span>}
        </div>
      )}
      {w?.rain && (
        <div style={{ ...row, gap: 8, marginBottom: 8, background: "#0e2438", border: `1px solid ${C.blue}55`, borderRadius: 10, padding: "6px 12px" }} data-testid="glass-rain-alert">
          <span style={{ fontSize: 18 }}>☔</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.blue }}>
            Через {w.rain.in_h === 1 ? "час" : `${w.rain.in_h} ч`} дождь ({w.rain.prob}%) — возьмите зонт
          </span>
        </div>
      )}
      <div style={{ flex: 1, display: "flex", gap: 24, minHeight: 0 }}>
        <div style={{ flex: 1.15, minWidth: 0 }}>
          <Label color={C.blue}>СОБЫТИЯ · {data.events.length}</Label>
          {data.events.length === 0 ? <div style={{ fontSize: 18, color: C.dim }}>Свободный день</div>
            : data.events.map((e, i) => (
              <div key={i} style={{ ...row, fontSize: 19, padding: "5px 0", borderBottom: i < data.events.length - 1 ? "1px solid #16191d" : "none" }}>
                <span style={{ color: C.blue, fontWeight: 800, minWidth: 58, ...NUM }}>{e.time || "· ·"}</span>
                <span style={ellipsis}>{e.title}</span>
              </div>
            ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Label color={C.yellow}>ЗАДАЧИ · {data.tasks.length}</Label>
          {data.tasks.length === 0 ? <div style={{ fontSize: 18, color: C.dim }}>Всё сделано</div>
            : data.tasks.map((t, i) => (
              <div key={i} style={{ ...row, fontSize: 19, padding: "5px 0", borderBottom: i < data.tasks.length - 1 ? "1px solid #16191d" : "none" }}>
                <span style={{ color: PRIO[t.priority] || C.dim, fontSize: 12 }}>⬤</span>
                <span style={ellipsis}>{t.title}</span>
              </div>
            ))}
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        {data.tip && (
          <div style={{ fontSize: 15, color: C.purple, marginBottom: 8, fontStyle: "italic" }} data-testid="glass-tip">✦ {data.tip}</div>
        )}
        <div style={{ ...row, justifyContent: "space-between", marginBottom: 5 }}>
          <span style={{ fontSize: 13, letterSpacing: 2.5, color: C.green, fontWeight: 800 }}>ПРИВЫЧКИ</span>
          <span style={{ fontSize: 17, fontWeight: 800, color: habitPct === 100 && data.habits.total > 0 ? C.green : C.text, ...NUM }}>{data.habits.done}/{data.habits.total}</span>
        </div>
        <Bar pct={habitPct} color={C.green} />
      </div>
    </div>
  );
}

function HealthScreen({ h }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-health">
      <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
        <Ring value={h.recovery} color={recColor(h.recovery)} caption="ВОССТАН." />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
          <StatRow label="НАГРУЗКА" value={h.strain != null ? h.strain : "—"} color={C.blue} />
          <div style={{ height: 1, background: C.faint }} />
          <StatRow label="СОН" value={h.sleep_performance != null ? `${Math.round(h.sleep_performance)}%` : "—"} color={C.yellow} />
        </div>
      </div>
      {h.advice && (
        <div style={{ marginTop: 20, borderLeft: `5px solid ${recColor(h.recovery)}`, paddingLeft: 14 }}>
          <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}>{h.advice.title}</div>
          <div style={{ fontSize: 17, color: C.dim, marginTop: 5, lineHeight: 1.4 }}>{h.advice.text}</div>
        </div>
      )}
    </div>
  );
}

function WorkoutScreen({ w }) {
  return (
    <div style={{ flex: 1, minHeight: 0 }} data-testid="glass-screen-workout">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.purple}>ТРЕНИРОВКА ДНЯ</Label>
        <span style={{ fontSize: 16, fontWeight: 800, color: w.done === w.total ? C.green : C.dim, ...NUM }}>{w.done}/{w.total}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 10, ...ellipsis }}>{w.title}</div>
      {w.items.map((i, idx) => (
        <div key={idx} style={{ ...row, fontSize: 19, padding: "6px 0", borderBottom: idx < w.items.length - 1 ? "1px solid #16191d" : "none" }}>
          <span style={{ color: i.done ? C.green : C.faint, fontWeight: 900, fontSize: 18, minWidth: 20 }}>{i.done ? "✓" : "○"}</span>
          <span style={{ ...ellipsis, flex: 1, color: i.done ? C.dim : C.text }}>{i.name}</span>
          {i.detail && <span style={{ color: C.dim, fontSize: 16, ...NUM, flexShrink: 0 }}>{i.detail}</span>}
        </div>
      ))}
    </div>
  );
}

function NutritionScreen({ n }) {
  const kcalPct = n.kcal_norm ? (n.kcal / n.kcal_norm) * 100 : 0;
  const protPct = n.protein_norm ? (n.protein / n.protein_norm) * 100 : 0;
  const ratio = n.kcal_norm ? n.kcal / n.kcal_norm : 0;
  const st = !n.kcal ? ["ПУСТО", C.dim, `норма ${n.kcal_norm || "—"} ккал`]
    : ratio > 1.05 ? ["↑ ПЕРЕБОР", C.red, `+${n.kcal - n.kcal_norm} ккал сверх нормы`]
    : ratio >= 0.95 ? ["✓ НОРМА ДОБРАНА", C.green, n.kcal > n.kcal_norm ? `+${n.kcal - n.kcal_norm} ккал` : "точно в цель"]
    : ["↓ НЕ ДОБРАЛ", C.blue, `осталось ${n.kcal_norm - n.kcal} ккал`];
  return (
    <div style={{ flex: 1, minHeight: 0 }} data-testid="glass-screen-nutrition">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.yellow}>ПИТАНИЕ · {n.meals} {n.meals === 1 ? "ПРИЁМ" : "ПРИЁМА"}</Label>
        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1, color: st[1] }} data-testid="glass-nutrition-status">{st[0]}</span>
      </div>
      <div style={{ ...row, gap: 8, margin: "2px 0 6px", padding: "6px 12px", borderRadius: 10,
                    background: `${st[1]}14`, border: `1px solid ${st[1]}44` }} data-testid="glass-nutrition-status-detail">
        <span style={{ fontSize: 17, fontWeight: 700, color: st[1], ...NUM }}>{st[2]}</span>
      </div>
      <div style={{ marginTop: 4 }}>
        <div style={{ ...row, justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 17, color: C.dim }}>Калории</span>
          <span style={{ fontSize: 30, fontWeight: 900, color: kcalPct > 110 ? C.red : C.text, ...NUM }}>
            {n.kcal}<span style={{ fontSize: 17, color: C.dim, fontWeight: 600 }}> / {n.kcal_norm || "—"}</span>
          </span>
        </div>
        <Bar pct={kcalPct} color={ratio > 1.05 ? C.red : ratio >= 0.95 ? C.green : C.blue} />
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ ...row, justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 17, color: C.dim }}>Белок, г</span>
          <span style={{ fontSize: 30, fontWeight: 900, color: C.blue, ...NUM }}>
            {n.protein}<span style={{ fontSize: 17, color: C.dim, fontWeight: 600 }}> / {n.protein_norm || "—"}</span>
          </span>
        </div>
        <Bar pct={protPct} color={C.blue} />
      </div>
    </div>
  );
}

function FamilyScreen({ fam }) {
  const distTxt = (d) => d == null ? "—" : d < 0.2 ? "рядом" : `${d} км`;
  const agoTxt = (m) => m == null ? "" : m < 2 ? "сейчас" : m < 60 ? `${m} мин назад` : `${Math.round(m / 60)} ч назад`;
  return (
    <div style={{ flex: 1, minHeight: 0 }} data-testid="glass-screen-family">
      <Label color={C.blue}>СЕМЬЯ</Label>
      {fam.map((m, i) => (
        <div key={i} style={{ padding: "8px 0", borderBottom: i < fam.length - 1 ? "1px solid #16191d" : "none" }}>
          <div style={{ ...row, fontSize: 20 }}>
            <span style={{ fontWeight: 700, flex: 1, ...ellipsis }}>{m.name}</span>
            <span style={{ color: m.dist != null && m.dist < 0.2 ? C.green : C.blue, fontWeight: 800, ...NUM }}>{distTxt(m.dist)}</span>
            <span style={{ color: C.dim, fontSize: 15, minWidth: 100, textAlign: "right", ...NUM }}>{agoTxt(m.mins)}</span>
            {m.battery != null && <span style={{ color: m.battery < 20 ? C.red : C.dim, fontSize: 15, minWidth: 42, textAlign: "right", ...NUM }}>{m.battery}%</span>}
          </div>
          {m.health && (
            <div style={{ ...row, gap: 18, fontSize: 15, marginTop: 4 }} data-testid={`glass-family-health-${i}`}>
              {m.health.recovery != null && <span style={{ color: C.dim }}>ВОССТ <span style={{ color: recColor(m.health.recovery), fontWeight: 800, fontSize: 17, ...NUM }}>{Math.round(m.health.recovery)}%</span></span>}
              {m.health.sleep != null && <span style={{ color: C.dim }}>СОН <span style={{ color: C.yellow, fontWeight: 800, fontSize: 17, ...NUM }}>{Math.round(m.health.sleep)}%</span></span>}
              {m.health.strain != null && <span style={{ color: C.dim }}>STRAIN <span style={{ color: C.blue, fontWeight: 800, fontSize: 17, ...NUM }}>{m.health.strain}</span></span>}
            </div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 14, color: C.faint, marginTop: 12 }}>Расстояние — от вашего телефона · метрики Whoop за сегодня</div>
    </div>
  );
}

function WeatherScreen({ w }) {
  return (
    <div style={{ flex: 1, minHeight: 0 }} data-testid="glass-screen-weather">
      <Label color={C.blue}>ПОГОДА{w.city ? ` · ${w.city.toUpperCase()}` : ""}</Label>
      <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 6 }}>
        <span style={{ fontSize: 84, lineHeight: 1 }}>{w.glyph}</span>
        <div>
          <div style={{ fontSize: 64, fontWeight: 900, lineHeight: 1, ...NUM }}>{w.temp > 0 ? `+${w.temp}` : w.temp}°</div>
          <div style={{ fontSize: 20, color: C.dim, marginTop: 4 }}>{w.text} · ощущается {w.feels > 0 ? `+${w.feels}` : w.feels}°</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 34, marginTop: 24 }}>
        <div><div style={{ fontSize: 14, color: C.dim, letterSpacing: 1.5 }}>ДНЁМ</div><div style={{ fontSize: 28, fontWeight: 800, ...NUM }}>{w.tmax > 0 ? `+${w.tmax}` : w.tmax}°</div></div>
        <div><div style={{ fontSize: 14, color: C.dim, letterSpacing: 1.5 }}>НОЧЬЮ</div><div style={{ fontSize: 28, fontWeight: 800, color: C.dim, ...NUM }}>{w.tmin > 0 ? `+${w.tmin}` : w.tmin}°</div></div>
        {w.precip != null && <div><div style={{ fontSize: 14, color: C.dim, letterSpacing: 1.5 }}>ОСАДКИ</div><div style={{ fontSize: 28, fontWeight: 800, color: C.blue, ...NUM }}>{w.precip}%</div></div>}
      </div>
    </div>
  );
}

function BriefingScreen({ text, name }) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }} data-testid="glass-screen-briefing">
      <Label color={C.yellow}>УТРЕННИЙ БРИФИНГ</Label>
      <p style={{ fontSize: text.length > 420 ? 18 : 20, lineHeight: 1.55, margin: 0, color: C.text }}>{text}</p>
    </div>
  );
}

const CARDINALS = ["С", "СВ", "В", "ЮВ", "Ю", "ЮЗ", "З", "СЗ"];

function calcDistKm(a, b) {
  const R = 6371, toR = (x) => (x * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function calcBearing(a, b) {
  const toR = (x) => (x * Math.PI) / 180;
  const dl = toR(b.lng - a.lng);
  const y = Math.sin(dl) * Math.cos(toR(b.lat));
  const x = Math.cos(toR(a.lat)) * Math.sin(toR(b.lat)) - Math.sin(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function useSmoothAngle(target) {
  const accRef = useRef(target);
  let delta = ((target - accRef.current) % 360 + 540) % 360 - 180;
  accRef.current += delta;
  return accRef.current;
}

function NavMap({ me, target, route, arrived, splitIdx, zoomTo }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const meRef = useRef(null);
  const lineRef = useRef(null);
  const tgtRef = useRef(null);
  const fittedRef = useRef(null);
  const zoomedRef = useRef(false);

  useEffect(() => {
    const m = L.map(divRef.current, { zoomControl: false, attributionControl: false, keyboard: false,
      dragging: false, scrollWheelZoom: false, touchZoom: false, doubleClickZoom: false, boxZoom: false });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { maxZoom: 20, subdomains: "abcd" }).addTo(m);
    m.setView([55.751, 37.618], 15);
    mapRef.current = m;
    return () => { m.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !target?.lat) return;
    tgtRef.current?.remove();
    tgtRef.current = L.marker([target.lat, target.lng], {
      icon: L.divIcon({ className: "", iconAnchor: [4, 26],
        html: '<div style="font-size:26px;line-height:1;filter:drop-shadow(0 0 8px rgba(255,82,82,.8))">🚩</div>' }),
    }).addTo(mapRef.current);
  }, [target?.lat, target?.lng]);

  useEffect(() => {
    if (!mapRef.current) return;
    lineRef.current?.remove();
    lineRef.current = null;
    if (route?.line?.length > 1) {
      const idx = Math.max(0, Math.min(splitIdx ?? 0, route.line.length - 1));
      const passed = route.line.slice(0, idx + 1);
      const ahead = route.line.slice(idx);
      const layers = [
        L.polyline(ahead, { color: "#00e676", weight: 7, opacity: 0.25 }),
        L.polyline(ahead, { color: "#00e676", weight: 4, opacity: 0.95, dashArray: route.fallback ? "10 10" : null }),
      ];
      if (passed.length > 1) layers.unshift(L.polyline(passed, { color: "#5a626b", weight: 3, opacity: 0.5 }));
      lineRef.current = L.layerGroup(layers).addTo(mapRef.current);
      const key = JSON.stringify([route.line[0], route.line.at(-1)]);
      if (fittedRef.current !== key) {
        fittedRef.current = key;
        mapRef.current.fitBounds(L.latLngBounds(route.line).pad(0.18), { maxZoom: 17 });
      }
    }
  }, [route, splitIdx]);

  // автозум к манёвру
  useEffect(() => {
    if (!mapRef.current) return;
    if (zoomTo) {
      if (!zoomedRef.current) {
        zoomedRef.current = true;
        mapRef.current.flyTo(zoomTo, 18, { duration: 0.9 });
      }
    } else if (zoomedRef.current) {
      zoomedRef.current = false;
      if (me) mapRef.current.flyTo([me.lat, me.lng], 16, { duration: 0.9 });
    }
  }, [zoomTo, me]);

  useEffect(() => {
    if (!mapRef.current || !me) return;
    const pos = [me.lat, me.lng];
    if (!meRef.current) {
      meRef.current = L.marker(pos, {
        icon: L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 11],
          html: `<div style="position:relative;width:22px;height:22px">
            <div style="position:absolute;inset:-9px;border-radius:50%;border:2px solid #54b6ff;animation:navPulse 1.6s ease-out infinite"></div>
            <div style="width:22px;height:22px;border-radius:50%;background:#54b6ff;border:3px solid #fff;box-shadow:0 0 14px #54b6ff"></div></div>` }),
        zIndexOffset: 500,
      }).addTo(mapRef.current);
    } else meRef.current.setLatLng(pos);
    if (!mapRef.current.getBounds().pad(-0.25).contains(pos)) mapRef.current.panTo(pos, { animate: true });
  }, [me, zoomTo]);

  return (
    <div style={{ position: "relative", flex: 1, minHeight: 0, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.faint}` }}>
      <div ref={divRef} style={{ position: "absolute", inset: 0, background: "#000" }} data-testid="glass-nav-map" />
      {arrived && (
        <div style={{ position: "absolute", inset: 0, zIndex: 800, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      background: "rgba(0,0,0,0.55)", backdropFilter: "blur(2px)" }} data-testid="glass-nav-arrived">
          <span style={{ fontSize: 58, color: C.green, fontWeight: 900, lineHeight: 1 }}>✓</span>
          <span style={{ fontSize: 30, color: C.green, fontWeight: 900, marginTop: 6 }}>ВЫ НА МЕСТЕ</span>
        </div>
      )}
    </div>
  );
}

const STEP_GLYPH = (mod) => {
  if (!mod) return "⬆";
  if (mod.includes("uturn")) return "⟲";
  if (mod.includes("left")) return "⬅";
  if (mod.includes("right")) return "➡";
  if (mod === "arrive") return "◎";
  return "⬆";
};

function NavScreen({ glassKey }) {
  const [nav, setNav] = useState(null);
  const [heading, setHeading] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [route, setRoute] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [hint, setHint] = useState(null);
  const [splitIdx, setSplitIdx] = useState(0);
  const [avgKmh, setAvgKmh] = useState(null);
  const lastRouteReq = useRef(null);
  const stepIdx = useRef(0);
  const announced = useRef({});
  const rerouting = useRef(false);
  const speedHist = useRef([]);
  const voiceRef = useRef(true);
  voiceRef.current = voiceOn;

  const speak = (text) => {
    if (!voiceRef.current || !window.speechSynthesis) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "ru-RU";
      u.rate = 1.05;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {}
  };

  useEffect(() => {
    const load = () => fetch(`${BASE}/api/glass/nav?key=${encodeURIComponent(glassKey)}`)
      .then((r) => r.json()).then(setNav).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [glassKey]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        if (p.coords.speed != null && !Number.isNaN(p.coords.speed)) setSpeed(p.coords.speed * 3.6);
      },
      () => {}, { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  useEffect(() => {
    const onOri = (e) => {
      const h = e.webkitCompassHeading != null ? e.webkitCompassHeading
        : e.absolute && e.alpha != null ? (360 - e.alpha) % 360 : null;
      if (h != null) setHeading(h);
    };
    window.addEventListener("deviceorientationabsolute", onOri, true);
    window.addEventListener("deviceorientation", onOri, true);
    return () => {
      window.removeEventListener("deviceorientationabsolute", onOri, true);
      window.removeEventListener("deviceorientation", onOri, true);
    };
  }, []);

  const local = myPos && nav?.target?.lat != null;
  const me = local ? myPos : nav?.me || null;

  // маршрут: запрашиваем при старте и при смене цели/режима; пока мы на маршруте — держим его
  useEffect(() => {
    if (!me || !nav?.target?.lat) return;
    const tkey = `${nav.target.lat},${nav.target.lng},${nav.target.mode}`;
    if (route && lastRouteReq.current === tkey) return;
    lastRouteReq.current = tkey;
    fetch(`${BASE}/api/glass/route?key=${encodeURIComponent(glassKey)}&from_lat=${me.lat}&from_lng=${me.lng}`)
      .then((r) => r.json()).then((d) => { if (d.line) setRoute(d); }).catch(() => {});
  }, [me?.lat, me?.lng, nav?.target?.lat, nav?.target?.lng, nav?.target?.mode, glassKey, route]);

  // Enter — вкл/выкл голос
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Enter") setVoiceOn((v) => !v); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); try { window.speechSynthesis?.cancel(); } catch {} };
  }, []);

  // сброс шагов при новом маршруте
  useEffect(() => {
    const key = route?.steps?.length ? JSON.stringify([route.line[0], route.line.at(-1)]) : null;
    if (announced.current._route !== key) {
      announced.current = { _route: key };
      stepIdx.current = 0;
      setSplitIdx(0);
    }
    rerouting.current = false;
  }, [route]);

  // средняя скорость за последние 3 мин → точный ETA
  useEffect(() => {
    if (!me) return;
    const now = Date.now();
    const h = speedHist.current;
    h.push({ t: now, lat: me.lat, lng: me.lng });
    while (h.length && now - h[0].t > 180000) h.shift();
    if (h.length >= 3 && now - h[0].t > 20000) {
      let d = 0;
      for (let i = 1; i < h.length; i++) d += calcDistKm(h[i - 1], h[i]);
      const kmh = d / ((now - h[0].t) / 3600000);
      setAvgKmh(kmh > 0.7 ? kmh : null);
    }
  }, [me?.lat, me?.lng]);

  // прогресс по линии + умный пересчёт при отклонении >40 м
  useEffect(() => {
    if (!route?.line?.length || !me) return;
    let best = 0, bestD = Infinity;
    for (let i = 0; i < route.line.length; i++) {
      const d = calcDistKm(me, { lat: route.line[i][0], lng: route.line[i][1] });
      if (d < bestD) { bestD = d; best = i; }
    }
    setSplitIdx(best);
    if (!route.fallback && bestD > 0.04 && !rerouting.current) {
      rerouting.current = true;
      speak("Пересчитываю маршрут");
      fetch(`${BASE}/api/glass/route?key=${encodeURIComponent(glassKey)}&from_lat=${me.lat}&from_lng=${me.lng}`)
        .then((r) => r.json()).then((d) => { if (d.line) setRoute(d); }).catch(() => { rerouting.current = false; });
    }
  }, [me, route]);

  // индекс точки линии для каждого шага (для надёжного пропуска пройденных манёвров)
  const stepLineIdx = useMemo(() => {
    if (!route?.steps?.length || !route?.line?.length) return [];
    return route.steps.map((st) => {
      let best = 0, bd = Infinity;
      for (let i = 0; i < route.line.length; i++) {
        const d = calcDistKm(st, { lat: route.line[i][0], lng: route.line[i][1] });
        if (d < bd) { bd = d; best = i; }
      }
      return best;
    });
  }, [route]);

  // пошаговые подсказки: «через N метров …» и в точке манёвра
  useEffect(() => {
    if (!route?.steps?.length || !me) { setHint(null); return; }
    let i = stepIdx.current;
    while (i < route.steps.length - 1 &&
           (calcDistKm(me, route.steps[i]) * 1000 < 22 || (stepLineIdx[i] != null && stepLineIdx[i] < splitIdx))) {
      i += 1;
      stepIdx.current = i;
    }
    const st = route.steps[i];
    const d = Math.round(calcDistKm(me, st) * 1000);
    setHint({ ...st, d });
    if (d > 35 && d < 150 && !announced.current["p" + i]) {
      announced.current["p" + i] = 1;
      speak(`Через ${Math.max(10, Math.round(d / 10) * 10)} метров ${st.text.toLowerCase()}`);
    } else if (d <= 35 && !announced.current["n" + i]) {
      announced.current["n" + i] = 1;
      speak(st.text);
      if (i < route.steps.length - 1) stepIdx.current = i + 1;
    }
  }, [me, route]);

  const smoothRot = useSmoothAngle(
    me && nav?.target?.lat ? calcBearing(me, nav.target) - (heading ?? 0) : 0);

  if (!nav) return <div style={{ fontSize: 22, color: C.dim }}>Загрузка…</div>;
  if (!nav.target) return (
    <div style={{ flex: 1 }} data-testid="glass-screen-nav">
      <Label color={C.purple}>МАРШРУТ</Label>
      <div style={{ fontSize: 20, color: C.dim }}>Точка снята с карты</div>
    </div>
  );

  const distKm = route && !route.fallback ? route.distance_m / 1000
    : me ? calcDistKm(me, nav.target) : nav.dist_km;
  const bearing = me ? calcBearing(me, nav.target) : nav.bearing;

  if (distKm == null || !me) return (
    <div style={{ flex: 1 }} data-testid="glass-screen-nav">
      <Label color={C.purple}>МАРШРУТ · {nav.target.name.toUpperCase()}</Label>
      <div style={{ fontSize: 20, color: C.dim, lineHeight: 1.5 }}>Ищу вашу геопозицию… Разрешите геолокацию или откройте LifeOS на телефоне.</div>
    </div>
  );

  const straight = calcDistKm(me, nav.target);
  const arrived = straight < 0.04;
  const mode = nav.target.mode || "foot";
  const MODE_SPEED = { foot: 5, bike: 15, car: 40 };
  const MODE_WORD = { foot: "пешком", bike: "на вело", car: "на авто" };

  let remainKm = distKm, progressPct = null;
  if (route?.line?.length > 1) {
    let total = 0, remain = 0;
    for (let i = 1; i < route.line.length; i++) {
      const seg = calcDistKm({ lat: route.line[i - 1][0], lng: route.line[i - 1][1] }, { lat: route.line[i][0], lng: route.line[i][1] });
      total += seg;
      if (i > splitIdx) remain += seg;
    }
    if (total > 0.01) {
      remainKm = remain;
      progressPct = Math.min(100, Math.max(0, Math.round((1 - remain / total) * 100)));
    }
  }
  const color = straight < 0.25 ? C.green : remainKm < 1 ? C.yellow : C.blue;
  const distTxt = remainKm < 1 ? `${Math.round(remainKm * 1000)} м` : `${remainKm.toFixed(remainKm < 10 ? 1 : 0)} км`;
  const etaMin = Math.max(1, Math.round(
    avgKmh ? (remainKm / avgKmh) * 60
      : route?.duration_s && route.distance_m ? (route.duration_s / 60) * (remainKm / (route.distance_m / 1000))
      : (remainKm / MODE_SPEED[mode]) * 60));
  const zoomTo = hint && !arrived && hint.d < 80 ? [hint.lat, hint.lng] : null;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, gap: 10 }} data-testid="glass-screen-nav">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.purple}>МАРШРУТ · {nav.target.name.toUpperCase()}</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>
          <span style={{ color: voiceOn ? C.green : C.dim }} data-testid="glass-voice-badge">{voiceOn ? "ГОЛОС ВКЛ" : "ГОЛОС ВЫКЛ"}</span>
          <span style={{ color: C.faint }}> · </span>
          <span style={{ color: local ? C.green : C.dim }}>{local ? "GPS ОЧКОВ" : "ПО ТЕЛЕФОНУ"}</span>
        </span>
      </div>
      <NavMap me={me} target={nav.target} route={route} arrived={arrived} splitIdx={splitIdx} zoomTo={zoomTo} />
      {hint && !arrived && (
        <div style={{ ...row, gap: 12, background: "#101317", borderRadius: 12, padding: "8px 14px", border: `1px solid ${C.faint}` }} data-testid="glass-nav-hint">
          <span style={{ fontSize: 26, color: C.green, lineHeight: 1 }}>{STEP_GLYPH(hint.mod)}</span>
          <span style={{ fontSize: 18, fontWeight: 700, ...ellipsis, flex: 1 }}>
            {hint.d > 25 ? <span style={{ color: C.dim, fontWeight: 600 }}>через {hint.d} м — </span> : null}{hint.text}
          </span>
        </div>
      )}
      <div style={{ ...row, gap: 16 }}>
        <div style={{ width: 54, height: 54, borderRadius: "50%", border: `2px solid ${C.faint}`, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ transform: `rotate(${smoothRot}deg)`, transition: "transform .5s ease", fontSize: 26, lineHeight: 1, color,
                        filter: `drop-shadow(0 0 8px ${color}66)` }} data-testid="glass-nav-arrow">▲</div>
        </div>
        <div style={{ fontSize: 38, fontWeight: 900, color, lineHeight: 1, ...NUM }} data-testid="glass-nav-dist">{arrived ? "0 м" : distTxt}</div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, ...NUM }}>≈ {etaMin} мин <span style={{ fontSize: 14, color: C.dim, fontWeight: 600 }}>{MODE_WORD[mode]}</span></div>
          <div style={{ fontSize: 14, color: C.dim, ...NUM }} data-testid="glass-nav-meta">
            {progressPct != null ? `пройдено ${progressPct}% · ` : ""}
            {CARDINALS[Math.round(bearing / 45) % 8]} · {Math.round(bearing)}°
            {avgKmh != null ? ` · ${avgKmh.toFixed(0)} км/ч` : speed != null && speed > 1 ? ` · ${speed.toFixed(0)} км/ч` : ""}
            {route?.fallback ? " · по прямой" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

function RunScreen({ glassKey, onActive }) {
  const [status, setStatus] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [distKm, setDistKm] = useState(0);
  const [msg, setMsg] = useState(null);
  const pointsRef = useRef([]);
  const distRef = useRef(0);
  const watchRef = useRef(null);
  const startRef = useRef(null);
  const maxSpd = useRef(0);
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const lineRef = useRef(null);
  const meRef = useRef(null);
  const statusRef = useRef("idle");
  statusRef.current = status;

  useEffect(() => {
    const m = L.map(divRef.current, { zoomControl: false, attributionControl: false, keyboard: false,
      dragging: false, scrollWheelZoom: false, touchZoom: false, doubleClickZoom: false, boxZoom: false });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { maxZoom: 20, subdomains: "abcd" }).addTo(m);
    m.setView([55.751, 37.618], 15);
    mapRef.current = m;
    navigator.geolocation?.getCurrentPosition((p) => mapRef.current?.setView([p.coords.latitude, p.coords.longitude], 16), () => {});
    return () => { m.remove(); mapRef.current = null; stopWatch(); onActive?.(false); };
  }, []);

  const stopWatch = () => {
    if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
  };

  const onPos = (p) => {
    const pt = { lat: p.coords.latitude, lng: p.coords.longitude, t: Date.now() };
    const pts = pointsRef.current;
    const last = pts.at(-1);
    if (last) {
      const seg = calcDistKm(last, pt);
      if (seg < 0.002) return;
      distRef.current += seg;
      setDistKm(distRef.current);
      const dt = (pt.t - last.t) / 3600000;
      if (dt > 0) maxSpd.current = Math.max(maxSpd.current, seg / dt);
    }
    pts.push(pt);
    if (mapRef.current) {
      if (!meRef.current) {
        meRef.current = L.marker([pt.lat, pt.lng], { zIndexOffset: 500,
          icon: L.divIcon({ className: "", iconSize: [20, 20], iconAnchor: [10, 10],
            html: `<div style="position:relative;width:20px;height:20px"><div style="position:absolute;inset:-8px;border-radius:50%;border:2px solid #00e676;animation:navPulse 1.6s ease-out infinite"></div><div style="width:20px;height:20px;border-radius:50%;background:#00e676;border:3px solid #fff;box-shadow:0 0 12px #00e676"></div></div>` }) }).addTo(mapRef.current);
      } else meRef.current.setLatLng([pt.lat, pt.lng]);
      if (!lineRef.current) lineRef.current = L.polyline(pts.map((x) => [x.lat, x.lng]), { color: "#00e676", weight: 4, opacity: 0.95 }).addTo(mapRef.current);
      else lineRef.current.addLatLng([pt.lat, pt.lng]);
      mapRef.current.panTo([pt.lat, pt.lng], { animate: true });
    }
  };

  useEffect(() => {
    if (status !== "running") return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [status]);

  const start = () => {
    pointsRef.current = [];
    lineRef.current?.remove(); lineRef.current = null;
    maxSpd.current = 0;
    distRef.current = 0;
    setDistKm(0); setElapsed(0); setMsg(null);
    startRef.current = Date.now();
    watchRef.current = navigator.geolocation?.watchPosition(onPos,
      () => { if (!pointsRef.current.length) setMsg("Нет GPS — разрешите геолокацию"); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 });
    setStatus("running");
    onActive?.(true);
  };

  const stop = async () => {
    stopWatch();
    setStatus("saving");
    onActive?.(false);
    const pts = pointsRef.current;
    const dist = distRef.current;
    const dur = Math.max(1, Math.round((Date.now() - startRef.current) / 1000));
    if (pts.length < 2 || dist < 0.02) { setStatus("idle"); setMsg("Слишком короткий трек — не сохранён"); return; }
    const avg = dist / (dur / 3600);
    try {
      const r = await fetch(`${BASE}/api/glass/track?key=${encodeURIComponent(glassKey)}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sport: avg < 7 ? "walk" : "run", points: pts, distance_m: dist * 1000,
          duration_s: dur, avg_speed_kmh: avg, max_speed_kmh: maxSpd.current,
          started_at: new Date(startRef.current).toISOString() }) });
      setMsg(r.ok ? "Трек сохранён в Здоровье" : "Не удалось сохранить");
    } catch { setMsg("Не удалось сохранить"); }
    setStatus("idle");
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      if (statusRef.current === "idle") start();
      else if (statusRef.current === "running") stop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const pace = distKm > 0.05 ? elapsed / 60 / distKm : null;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, gap: 10 }} data-testid="glass-screen-run">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.green}>ТРЕНИРОВКА GPS</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: status === "running" ? C.green : C.dim }} data-testid="glass-run-status">
          {status === "running" ? "● ЗАПИСЬ · ENTER — СТОП" : "ENTER — СТАРТ"}
        </span>
      </div>
      <div style={{ position: "relative", flex: 1, minHeight: 0, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.faint}` }}>
        <div ref={divRef} style={{ position: "absolute", inset: 0, background: "#000" }} />
        {msg && <div style={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 800, textAlign: "center",
                              background: "rgba(0,0,0,.7)", borderRadius: 8, padding: "5px 10px", fontSize: 15, color: C.yellow }} data-testid="glass-run-msg">{msg}</div>}
      </div>
      <div style={{ ...row, gap: 22 }}>
        <div><div style={{ fontSize: 12, color: C.dim, letterSpacing: 1.5 }}>ВРЕМЯ</div>
          <div style={{ fontSize: 32, fontWeight: 900, ...NUM }} data-testid="glass-run-time">{mm}:{ss}</div></div>
        <div><div style={{ fontSize: 12, color: C.dim, letterSpacing: 1.5 }}>ДИСТАНЦИЯ</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: C.green, ...NUM }} data-testid="glass-run-dist">{distKm.toFixed(2)} км</div></div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}><div style={{ fontSize: 12, color: C.dim, letterSpacing: 1.5 }}>ТЕМП</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: C.blue, ...NUM }}>{pace ? `${Math.floor(pace)}:${String(Math.round((pace % 1) * 60)).padStart(2, "0")}` : "—"} <span style={{ fontSize: 14, color: C.dim }}>/км</span></div></div>
      </div>
    </div>
  );
}

const RADAR_COLORS = [C.blue, C.green, C.yellow, C.purple, C.red];

function RadarScreen({ glassKey }) {
  const [radar, setRadar] = useState(null);
  const [heading, setHeading] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [alert, setAlert] = useState(null);
  const prevD = useRef({});

  useEffect(() => {
    const load = () => fetch(`${BASE}/api/glass/radar?key=${encodeURIComponent(glassKey)}`)
      .then((r) => r.json()).then(setRadar).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [glassKey]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  useEffect(() => {
    const onOri = (e) => {
      const h = e.webkitCompassHeading != null ? e.webkitCompassHeading
        : e.absolute && e.alpha != null ? (360 - e.alpha) % 360 : null;
      if (h != null) setHeading(h);
    };
    window.addEventListener("deviceorientationabsolute", onOri, true);
    window.addEventListener("deviceorientation", onOri, true);
    return () => {
      window.removeEventListener("deviceorientationabsolute", onOri, true);
      window.removeEventListener("deviceorientation", onOri, true);
    };
  }, []);

  const me = myPos || radar?.me;
  const members = useMemo(() => (radar?.members || []).map((m, i) => {
    const dist = me ? calcDistKm(me, m) : (m.dist_km ?? null);
    const brg = me ? calcBearing(me, m) : (m.bearing ?? 0);
    return { ...m, dist, brg, color: RADAR_COLORS[i % RADAR_COLORS.length] };
  }), [radar, me?.lat, me?.lng]);

  useEffect(() => {
    for (const m of members) {
      if (m.dist == null) continue;
      const prev = prevD.current[m.name];
      if (prev != null && prev > 0.55 && m.dist <= 0.5) {
        const msg = `${m.name} приближается — ${Math.round(m.dist * 1000)} метров`;
        setAlert(msg);
        speakRu(msg);
        setTimeout(() => setAlert(null), 12000);
      }
      prevD.current[m.name] = m.dist;
    }
  }, [members]);

  const size = 330, cx = size / 2, cy = size / 2, maxR = 148;
  const rOf = (d) => {
    const t = Math.min(1, Math.max(0, (Math.log10(Math.max(d ?? 50, 0.05)) + 1.301) / 3));
    return 28 + t * (maxR - 28);
  };
  const hd = heading ?? 0;
  const pos = (brg, r) => {
    const a = ((brg - hd) * Math.PI) / 180;
    return [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  };
  const distTxt = (d) => d == null ? "—" : d < 0.2 ? "рядом" : d < 1 ? `${Math.round(d * 1000)} м` : `${d.toFixed(d < 10 ? 1 : 0)} км`;
  const agoTxt = (m) => m == null ? "" : m < 2 ? "сейчас" : m < 60 ? `${m} мин` : `${Math.round(m / 60)} ч`;
  const [nx, ny] = pos(0, maxR - 10);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-radar">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.green}>РАДАР СЕМЬИ</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: heading != null ? C.green : C.dim }}>
          {heading != null ? "ПО КОМПАСУ" : "СЕВЕР ВВЕРХУ"}
        </span>
      </div>
      {alert && (
        <div style={{ ...row, gap: 8, marginBottom: 6, background: "#0e2e1a", border: `1px solid ${C.green}66`,
                      borderRadius: 10, padding: "6px 12px", fontSize: 16, fontWeight: 700, color: C.green }} data-testid="glass-radar-alert">
          ◉ {alert}
        </div>
      )}
      <div style={{ flex: 1, display: "flex", gap: 18, minHeight: 0, alignItems: "center" }}>
        <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
          <svg width={size} height={size} style={{ position: "absolute", inset: 0 }}>
            {[rOf(0.5), rOf(5), maxR].map((r, i) => (
              <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke="#1e2731" strokeWidth="1.5" />
            ))}
            <line x1={cx} y1={cy - maxR} x2={cx} y2={cy + maxR} stroke="#151b22" strokeWidth="1" />
            <line x1={cx - maxR} y1={cy} x2={cx + maxR} y2={cy} stroke="#151b22" strokeWidth="1" />
            {[["500м", rOf(0.5)], ["5км", rOf(5)], ["50км", maxR]].map(([t, r], i) => (
              <text key={i} x={cx + 4} y={cy - r + 13} fill="#3f4a56" fontSize="11" fontWeight="700">{t}</text>
            ))}
            <text x={nx} y={ny} fill={C.red} fontSize="14" fontWeight="900" textAnchor="middle">С</text>
            {members.filter((m) => m.dist != null).map((m, i) => {
              const [x, y] = pos(m.brg, rOf(m.dist));
              return (
                <g key={i} data-testid={`glass-radar-dot-${i}`}>
                  <circle cx={x} cy={y} r="7" fill={m.color} style={{ filter: `drop-shadow(0 0 6px ${m.color})` }} />
                  <text x={x} y={y - 12} fill={m.color} fontSize="13" fontWeight="800" textAnchor="middle">{m.name.split(" ")[0]}</text>
                </g>
              );
            })}
            <circle cx={cx} cy={cy} r="6" fill="#fff" />
            <circle cx={cx} cy={cy} r="10" fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.5" />
          </svg>
          <div style={{ position: "absolute", left: cx - maxR, top: cy - maxR, width: maxR * 2, height: maxR * 2,
                        borderRadius: "50%", overflow: "hidden", pointerEvents: "none" }}>
            <div style={{ position: "absolute", inset: 0, background: "conic-gradient(rgba(0,230,118,.22), transparent 75deg)",
                          animation: "radarSweep 4s linear infinite" }} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {members.length === 0 && <div style={{ fontSize: 17, color: C.dim, lineHeight: 1.4 }}>Нет данных о геопозиции семьи</div>}
          {members.map((m, i) => (
            <div key={i} style={{ padding: "7px 0", borderBottom: i < members.length - 1 ? "1px solid #16191d" : "none" }}>
              <div style={{ ...row, gap: 8 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                <span style={{ fontSize: 17, fontWeight: 700, ...ellipsis }}>{m.name.split(" ")[0]}</span>
              </div>
              <div style={{ fontSize: 15, color: C.dim, marginLeft: 17, ...NUM }}>
                <span style={{ color: m.dist != null && m.dist < 0.2 ? C.green : C.blue, fontWeight: 800 }}>{distTxt(m.dist)}</span>
                {m.dist != null && ` · ${CARDINALS[Math.round(m.brg / 45) % 8]}`}
                {m.mins != null && ` · ${agoTxt(m.mins)}`}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 13, color: C.faint, marginTop: 4 }}>Оповещение голосом, когда кто-то ближе 500 м</div>
    </div>
  );
}

function VoiceScreen({ glassKey, onActive }) {
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const statusRef = useRef("idle");
  statusRef.current = status;

  const start = async () => {
    setErr(null); setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setStatus("proc");
        try {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "cmd.webm");
          const r = await fetch(`${BASE}/api/glass/voice?key=${encodeURIComponent(glassKey)}`, { method: "POST", body: fd });
          const d = await r.json();
          if (!r.ok) throw new Error(d.detail || "Ошибка");
          setResult(d);
          speakRu(d.message);
        } catch (e) { setErr(e.message || "Не удалось распознать"); }
        setStatus("done");
        setTimeout(() => { if (statusRef.current === "done") { setStatus("idle"); onActive?.(false); } }, 10000);
      };
      rec.start();
      recRef.current = rec;
      setStatus("rec");
      onActive?.(true);
    } catch { setErr("Нет доступа к микрофону — разрешите его в браузере очков"); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      if (statusRef.current === "idle") start();
      else if (statusRef.current === "rec") recRef.current?.stop();
      else if (statusRef.current === "done") { setStatus("idle"); setResult(null); setErr(null); onActive?.(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch {}
      onActive?.(false);
    };
  }, []);

  const micColor = status === "rec" ? C.red : status === "proc" ? C.yellow : C.green;
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-voice">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.green}>ГОЛОСОВАЯ КОМАНДА</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: status === "rec" ? C.red : C.dim }} data-testid="glass-voice-status">
          {status === "rec" ? "● ЗАПИСЬ · ENTER — СТОП" : status === "proc" ? "РАСПОЗНАЮ…" : "ENTER — ГОВОРИТЬ"}
        </span>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}>
        <div style={{ width: 110, height: 110, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                      border: `3px solid ${micColor}`, boxShadow: `0 0 26px ${micColor}44`,
                      animation: status === "rec" ? "micPulse 1.1s ease-in-out infinite" : "none" }}>
          <span style={{ fontSize: 48, lineHeight: 1 }}>🎤</span>
        </div>
        {status === "idle" && !result && !err && (
          <div style={{ fontSize: 17, color: C.dim, textAlign: "center", lineHeight: 1.5, maxWidth: 480 }}>
            Нажмите Enter и скажите:<br />
            <span style={{ color: C.text }}>«Купить молоко»</span> — задача · <span style={{ color: C.text }}>«Завтра в 15:00 встреча»</span> — календарь<br />
            <span style={{ color: C.text }}>«Потратил 500 на обед»</span> — бюджет · <span style={{ color: C.text }}>«Отметь зарядку»</span> — привычка
          </div>
        )}
        {status === "rec" && <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>Слушаю…</div>}
        {status === "proc" && <div style={{ fontSize: 20, fontWeight: 700, color: C.yellow }}>Распознаю и добавляю…</div>}
        {result && (
          <div style={{ textAlign: "center", maxWidth: 480 }} data-testid="glass-voice-result">
            <div style={{ fontSize: 15, color: C.dim, fontStyle: "italic", marginBottom: 6 }}>«{result.transcript}»</div>
            <div style={{ fontSize: 21, fontWeight: 800, color: result.ok ? C.green : C.yellow, lineHeight: 1.35 }}>
              {result.ok ? "✓ " : ""}{result.message}
            </div>
          </div>
        )}
        {err && <div style={{ fontSize: 18, color: C.red, textAlign: "center", maxWidth: 440 }} data-testid="glass-voice-error">{err}</div>}
      </div>
    </div>
  );
}

function DebriefScreen({ glassKey, onActive }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const stRef = useRef({});
  stRef.current = { d, busy, playing };

  useEffect(() => {
    fetch(`${BASE}/api/glass/debrief?key=${encodeURIComponent(glassKey)}`)
      .then((r) => r.json()).then(setD).catch(() => setD({ exists: false }));
    return () => { audioRef.current?.pause(); onActive?.(false); };
  }, [glassKey]);

  const generate = async () => {
    setBusy(true);
    onActive?.(true);
    try {
      const r = await fetch(`${BASE}/api/glass/debrief?key=${encodeURIComponent(glassKey)}&generate=1`);
      setD(await r.json());
    } catch {}
    setBusy(false);
    onActive?.(false);
  };

  const togglePlay = async () => {
    if (stRef.current.playing) { audioRef.current?.pause(); return; }
    try {
      if (!audioRef.current) {
        const r = await fetch(`${BASE}/api/glass/debrief/audio?key=${encodeURIComponent(glassKey)}`);
        if (!r.ok) throw new Error();
        const audio = new Audio(URL.createObjectURL(await r.blob()));
        audio.onplay = () => { setPlaying(true); onActive?.(true); };
        audio.onpause = () => { setPlaying(false); onActive?.(false); };
        audio.onended = () => { setPlaying(false); onActive?.(false); };
        audioRef.current = audio;
      }
      audioRef.current.play();
    } catch {}
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      const s = stRef.current;
      if (s.busy) return;
      if (!s.d?.exists) generate();
      else if (s.d.has_audio) togglePlay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-debrief">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.purple}>ВЕЧЕРНИЙ ДЕБРИФ</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: playing ? C.green : C.dim }} data-testid="glass-debrief-status">
          {busy ? "СОБИРАЮ…" : !d?.exists ? "ENTER — СОБРАТЬ" : d.has_audio ? (playing ? "▶ ИГРАЕТ · ENTER — ПАУЗА" : "ENTER — СЛУШАТЬ") : ""}
        </span>
      </div>
      {!d ? <div style={{ fontSize: 20, color: C.dim }}>Загрузка…</div>
        : busy ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: C.yellow }}>Подвожу итоги дня…</div>
        : !d.exists ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <span style={{ fontSize: 52 }}>🌙</span>
            <div style={{ fontSize: 20, color: C.dim, textAlign: "center", lineHeight: 1.5 }}>
              Нажмите Enter — соберу итоги дня<br />и рекомендацию на завтра
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <p style={{ fontSize: d.text.length > 420 ? 18 : 20, lineHeight: 1.55, margin: 0, color: C.text }} data-testid="glass-debrief-text">{d.text}</p>
          </div>
        )}
    </div>
  );
}

const HR_ZONES_UI = [[0.5, "РАЗМИНКА", "#6b7683"], [0.6, "ЖИРОСЖИГ.", C.blue], [0.7, "КАРДИО", C.green], [0.8, "ТЕМП", C.yellow], [0.9, "МАКСИМУМ", C.red]];

function PulseScreen({ glassKey }) {
  const [p, setP] = useState(null);
  const lastZoneRef = useRef(null);

  useEffect(() => {
    const load = () => fetch(`${BASE}/api/glass/pulse?key=${encodeURIComponent(glassKey)}`)
      .then((r) => r.json()).then(setP).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [glassKey]);

  const zoneOf = (hr) => {
    if (!hr || !p?.hr_max) return null;
    const r = hr / p.hr_max;
    let z = null;
    HR_ZONES_UI.forEach((x, i) => { if (r >= x[0]) z = { ...{ idx: i }, name: x[1], color: x[2] }; });
    return z || { idx: -1, name: "ОТДЫХ", color: C.dim };
  };
  const wk = p?.workout;
  const zone = wk ? zoneOf(wk.avg_hr) : null;

  useEffect(() => {
    if (!wk?.active || !zone) return;
    if (lastZoneRef.current != null && lastZoneRef.current !== zone.idx) {
      speakRu(`Зона ${zone.idx + 1}: ${zone.name.toLowerCase().replace(".", "")}`);
    }
    lastZoneRef.current = zone.idx;
  }, [zone?.idx, wk?.active]);

  if (!p) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: C.dim }} data-testid="glass-screen-pulse">Загрузка…</div>;
  if (!p.connected) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: C.dim }} data-testid="glass-screen-pulse">Whoop не подключен</div>;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-pulse">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.red}>ПУЛЬС · WHOOP</Label>
        {wk && <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: wk.active ? C.green : C.dim }}>
          {wk.active ? "● ИДЁТ СИНХРОНИЗАЦИЯ" : `ЗАВЕРШЕНА ${wk.ended_min < 60 ? `${wk.ended_min} МИН` : `${Math.round(wk.ended_min / 60)} Ч`} НАЗАД`}
        </span>}
      </div>
      {wk ? (
        <>
          <div style={{ flex: 1, display: "flex", gap: 26, alignItems: "center", minHeight: 0 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 96, fontWeight: 900, lineHeight: 1, color: zone?.color || C.text, ...NUM }} data-testid="glass-pulse-hr">
                {wk.avg_hr ?? "—"}
              </div>
              <div style={{ fontSize: 15, color: C.dim, marginTop: 4 }}>ср. пульс · {zone && <b style={{ color: zone.color }}>{zone.name}</b>}</div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
              <StatRow label="ТРЕНИРОВКА" value={wk.sport || "—"} color={C.purple} />
              <div style={{ height: 1, background: C.faint }} />
              <StatRow label="МАКС. ПУЛЬС" value={wk.max_hr ?? "—"} color={C.red} />
              <div style={{ height: 1, background: C.faint }} />
              <StatRow label="НАГРУЗКА" value={wk.strain ?? "—"} color={C.blue} />
              <div style={{ height: 1, background: C.faint }} />
              <StatRow label="ККАЛ" value={wk.kcal ?? "—"} color={C.yellow} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 8 }} data-testid="glass-pulse-zonebar">
            {HR_ZONES_UI.map((z, i) => (
              <div key={i} style={{ flex: 1, height: 10, borderRadius: 5, background: zone && zone.idx === i ? z[2] : "#1e2731",
                                    boxShadow: zone && zone.idx === i ? `0 0 10px ${z[2]}88` : "none" }} />
            ))}
          </div>
        </>
      ) : (
        <div style={{ flex: 1, display: "flex", gap: 26, alignItems: "center" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 96, fontWeight: 900, lineHeight: 1, color: C.blue, ...NUM }} data-testid="glass-pulse-hr">{p.rhr ?? "—"}</div>
            <div style={{ fontSize: 15, color: C.dim, marginTop: 4 }}>пульс покоя</div>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
            <StatRow label="ВОССТАНОВЛЕНИЕ" value={p.recovery != null ? `${Math.round(p.recovery)}%` : "—"} color={recColor(p.recovery)} />
            <div style={{ height: 1, background: C.faint }} />
            <StatRow label="НАГРУЗКА ДНЯ" value={p.day?.strain ?? "—"} color={C.blue} />
            <div style={{ height: 1, background: C.faint }} />
            <StatRow label="ККАЛ ЗА ДЕНЬ" value={p.day?.kcal ?? "—"} color={C.yellow} />
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, color: C.faint, marginTop: 6 }}>Данные приходят по мере синхронизации Whoop</div>
    </div>
  );
}

const BREATH_PHASES = [["ВДОХ", 4], ["ЗАДЕРЖКА", 7], ["ВЫДОХ", 8]];
const BREATH_CYCLES = 5;

function BreathScreen({ onActive }) {
  const [step, setStep] = useState(-1);
  const [done, setDone] = useState(false);
  const stepRef = useRef(-1);
  stepRef.current = step;

  useEffect(() => {
    if (step < 0 || step >= BREATH_PHASES.length * BREATH_CYCLES) return;
    const [name, dur] = BREATH_PHASES[step % 3];
    speakRu(name === "ВДОХ" ? "Вдох" : name === "ЗАДЕРЖКА" ? "Держим" : "Выдох");
    const t = setTimeout(() => {
      const next = step + 1;
      if (next >= BREATH_PHASES.length * BREATH_CYCLES) {
        setStep(-1); setDone(true); onActive?.(false);
        speakRu("Готово. Отличная работа");
        setTimeout(() => setDone(false), 8000);
      } else setStep(next);
    }, dur * 1000);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      if (stepRef.current < 0) { setDone(false); setStep(0); onActive?.(true); }
      else { setStep(-1); onActive?.(false); window.speechSynthesis?.cancel(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); onActive?.(false); window.speechSynthesis?.cancel(); };
  }, []);

  const active = step >= 0;
  const [phName, phDur] = active ? BREATH_PHASES[step % 3] : ["", 0];
  const cycle = active ? Math.floor(step / 3) + 1 : 0;
  const scale = !active ? 1 : phName === "ВДОХ" ? 1.45 : phName === "ЗАДЕРЖКА" ? 1.45 : 1;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-breath">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.blue}>ДЫХАНИЕ 4·7·8</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: active ? C.blue : C.dim }} data-testid="glass-breath-status">
          {active ? `ЦИКЛ ${cycle}/${BREATH_CYCLES} · ENTER — СТОП` : "ENTER — НАЧАТЬ"}
        </span>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <div style={{ width: 150, height: 150, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                      border: `3px solid ${C.blue}`, background: `${C.blue}18`, boxShadow: `0 0 34px ${C.blue}44`,
                      transform: `scale(${scale})`, transition: `transform ${phDur || 0.6}s ease-in-out` }}>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 2, color: C.blue }} data-testid="glass-breath-phase">
            {active ? phName : done ? "ГОТОВО" : "4·7·8"}
          </span>
        </div>
        {!active && !done && (
          <div style={{ fontSize: 17, color: C.dim, textAlign: "center", lineHeight: 1.5, maxWidth: 420, marginTop: 24 }}>
            Вдох 4 сек · задержка 7 сек · выдох 8 сек.<br />5 циклов ≈ полторы минуты — снижает пульс и стресс
          </div>
        )}
        {done && <div style={{ fontSize: 19, fontWeight: 700, color: C.green, marginTop: 24 }}>✓ Сессия завершена</div>}
      </div>
    </div>
  );
}

function EtaScreen({ glassKey }) {
  const [eta, setEta] = useState(null);
  const [sent, setSent] = useState(false);
  const posRef = useRef(null);
  const etaRef = useRef(null);
  etaRef.current = eta;

  useEffect(() => {
    let watchId = null;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (p) => { posRef.current = { lat: p.coords.latitude, lng: p.coords.longitude }; },
        () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    }
    const load = () => {
      const q = posRef.current ? `&lat=${posRef.current.lat}&lng=${posRef.current.lng}` : "";
      fetch(`${BASE}/api/glass/eta?key=${encodeURIComponent(glassKey)}${q}`)
        .then((r) => r.json()).then(setEta).catch(() => {});
    };
    load();
    const t1 = setTimeout(load, 4000);
    const t = setInterval(load, 45000);
    return () => { clearInterval(t); clearTimeout(t1); if (watchId != null) navigator.geolocation.clearWatch(watchId); };
  }, [glassKey]);

  useEffect(() => {
    const onKey = async (e) => {
      if (e.key !== "Enter") return;
      const d = etaRef.current;
      if (!d || d.at_home || (!d.foot_min && !d.car_min)) return;
      try {
        await fetch(`${BASE}/api/glass/eta/send?key=${encodeURIComponent(glassKey)}&foot_min=${d.foot_min || 0}&car_min=${d.car_min || 0}`, { method: "POST" });
        setSent(true);
        speakRu("Семья предупреждена");
        setTimeout(() => setSent(false), 15000);
      } catch {}
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [glassKey]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="glass-screen-eta">
      <div style={{ ...row, justifyContent: "space-between" }}>
        <Label color={C.green}>ДОМОЙ</Label>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: sent ? C.green : C.dim }} data-testid="glass-eta-status">
          {sent ? "✓ СЕМЬЯ ПРЕДУПРЕЖДЕНА" : eta?.foot_min || eta?.car_min ? "ENTER — СООБЩИТЬ СЕМЬЕ" : ""}
        </span>
      </div>
      {!eta ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: C.dim }}>Загрузка…</div>
        : !eta.home ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <span style={{ fontSize: 46 }}>🏠</span>
            <div style={{ fontSize: 18, color: C.dim, textAlign: "center", lineHeight: 1.5 }}>Добавьте зону «Дом» на семейной карте,<br />чтобы видеть время до дома</div>
          </div>
        ) : eta.at_home ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <span style={{ fontSize: 52 }}>🏠</span>
            <div style={{ fontSize: 26, fontWeight: 800, color: C.green }}>Вы дома</div>
          </div>
        ) : eta.no_location ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: C.dim }}>Нет геопозиции — разрешите GPS</div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
            <div style={{ ...row, gap: 10, fontSize: 19, color: C.dim, marginBottom: 8 }}>
              <span style={{ fontSize: 26 }}>🏠</span>
              <span style={{ fontWeight: 700, color: C.text }}>{eta.home}</span>
              <span style={{ ...NUM }}>· {eta.dist_km} км</span>
            </div>
            <div style={{ display: "flex", gap: 18 }}>
              {[["🚶", "ПЕШКОМ", eta.foot_min], ["🚗", "НА МАШИНЕ", eta.car_min]].map(([ico, lbl, min], i) => (
                <div key={i} style={{ flex: 1, background: "#10151b", border: "1px solid #1e2731", borderRadius: 14, padding: "14px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 26 }}>{ico}</div>
                  <div style={{ fontSize: 44, fontWeight: 900, lineHeight: 1.1, color: i === 0 ? C.green : C.blue, ...NUM }} data-testid={`glass-eta-${i === 0 ? "foot" : "car"}`}>
                    {min != null ? min : "—"}
                  </div>
                  <div style={{ fontSize: 13, color: C.dim, letterSpacing: 1.5, fontWeight: 700 }}>{lbl} · МИН</div>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}

function EveningScreen({ data }) {
  const ev = data.evening;
  return (
    <div style={{ flex: 1, minHeight: 0 }} data-testid="glass-screen-evening">
      <Label color={C.purple}>ИТОГ ДНЯ</Label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 30px", marginTop: 8 }}>
        <StatRow label="ЗАДАЧ ЗАКРЫТО" value={ev.tasks_done} color={C.green} />
        <StatRow label="ПРИВЫЧКИ" value={`${data.habits.done}/${data.habits.total}`} color={C.green} />
        <StatRow label="ПОТРАЧЕНО" value={`${ev.spent.toLocaleString("ru-RU")} ₽`} color={C.yellow} />
        <StatRow label="КАЛОРИИ" value={ev.kcal || "—"} color={C.blue} />
      </div>
      {ev.sleep != null && (
        <div style={{ marginTop: 22, borderLeft: `5px solid ${C.blue}`, paddingLeft: 14 }}>
          <div style={{ fontSize: 18, color: C.dim }}>Сон прошлой ночью: <span style={{ color: C.yellow, fontWeight: 800 }}>{Math.round(ev.sleep)}%</span></div>
        </div>
      )}
      <div style={{ fontSize: 16, color: C.dim, marginTop: 18, lineHeight: 1.4 }}>Хорошего вечера! Не забудьте отметить привычки и лечь пораньше.</div>
    </div>
  );
}

export default function GlassPage() {
  const [key, setKey] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [idx, setIdx] = useState(0);
  const [clock, setClock] = useState("");
  const [runActive, setRunActive] = useState(false);

  useEffect(() => {
    const m = document.querySelector('meta[name="viewport"]');
    const prev = m?.getAttribute("content");
    m?.setAttribute("content", "width=600, height=600, initial-scale=1.0, maximum-scale=1.0, user-scalable=no");
    document.body.style.background = "#000";
    const url = new URLSearchParams(window.location.search).get("key");
    if (url) localStorage.setItem("glass_key", url);
    setKey(url || localStorage.getItem("glass_key") || "");
    const tick = () => setClock(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
    tick();
    const t = setInterval(tick, 10000);
    return () => { clearInterval(t); if (prev) m?.setAttribute("content", prev); document.body.style.background = ""; };
  }, []);

  const load = useCallback(async (k) => {
    if (!k) return;
    try {
      const r = await fetch(`${BASE}/api/glass/data?key=${encodeURIComponent(k)}`);
      if (!r.ok) { setErr(r.status === 401 ? "Ключ недействителен — создайте новый в приложении LifeOS" : "Ошибка загрузки"); return; }
      setData(await r.json());
      setErr(null);
    } catch { setErr("Нет связи с LifeOS"); }
  }, []);

  useEffect(() => {
    if (!key) return;
    load(key);
    const t = setInterval(() => load(key), 60000);
    return () => clearInterval(t);
  }, [key, load]);

  // умная карусель: пропуск пустых экранов + порядок по времени суток
  const screens = useMemo(() => {
    if (!data) return [];
    const hour = new Date().getHours();
    const s = [{ id: "today", name: "СЕГОДНЯ" }];
    if (data.health) s.push({ id: "health", name: "ЗДОРОВЬЕ" });
    if (data.health) s.push({ id: "pulse", name: "ПУЛЬС" });
    if (data.workout) s.push({ id: "workout", name: "ТРЕНИРОВКА" });
    if (data.nutrition) s.push({ id: "nutrition", name: "ПИТАНИЕ" });
    if (data.family?.length) s.push({ id: "family", name: "СЕМЬЯ" });
    if (data.family?.length) s.push({ id: "radar", name: "РАДАР" });
    if (data.has_family) s.push({ id: "eta", name: "ДОМОЙ" });
    if (data.weather) s.push({ id: "weather", name: "ПОГОДА" });
    if (data.nav) s.push({ id: "nav", name: "МАРШРУТ" });
    s.push({ id: "run", name: "ТРЕНИРОВКА" });
    s.push({ id: "voice", name: "КОМАНДА" });
    s.push({ id: "breath", name: "ДЫХАНИЕ" });
    if (hour < 12) {
      const hi = s.findIndex((x) => x.id === "health");
      if (hi > 0) s.unshift(s.splice(hi, 1)[0]);
      if (data.briefing) s.unshift({ id: "briefing", name: "БРИФИНГ" });
    }
    if (hour >= 20) s.unshift({ id: "debrief", name: "ДЕБРИФ" });
    if (hour >= 21) s.unshift({ id: "evening", name: "ИТОГ ДНЯ" });
    return s;
  }, [data]);

  const n = screens.length;
  const cur = screens[Math.min(idx, Math.max(0, n - 1))];

  useEffect(() => { if (idx >= n && n > 0) setIdx(0); }, [n, idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % Math.max(1, n));
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + Math.max(1, n)) % Math.max(1, n));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [n]);

  useEffect(() => {
    if (n < 2 || cur?.id === "nav" || (runActive && ["run", "voice", "debrief", "breath"].includes(cur?.id))) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % n), ROTATE_MS);
    return () => clearTimeout(t);
  }, [idx, n, cur?.id, runActive]);

  const wrap = { width: 600, height: 600, background: C.bg, color: C.text, margin: "0 auto", overflow: "hidden",
                 display: "flex", flexDirection: "column", padding: "20px 30px 12px", boxSizing: "border-box", position: "relative",
                 fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" };

  if (key === null) return <div style={wrap} />;

  if (!key || (err && !data)) {
    return (
      <div style={wrap} data-testid="glass-setup">
        <div style={{ fontSize: 30, fontWeight: 800, color: C.green, letterSpacing: 1 }}>LifeOS</div>
        <div style={{ fontSize: 22, marginTop: 26, lineHeight: 1.45 }}>{err || "Нет ключа доступа."}</div>
        <div style={{ fontSize: 18, marginTop: 20, color: C.dim, lineHeight: 1.55 }}>
          Откройте LifeOS на телефоне → Обзор → карточка «Очки Meta Display» → создайте ключ и добавьте ссылку в Meta AI app.
        </div>
      </div>
    );
  }

  if (!data || !cur) {
    return <div style={{ ...wrap, justifyContent: "center", alignItems: "center" }} data-testid="glass-loading">
      <div style={{ fontSize: 24, color: C.dim }}>Загрузка…</div>
    </div>;
  }

  const h = data.health;
  const dateLabel = new Date(data.date + "T00:00:00").toLocaleDateString("ru-RU", { weekday: "short", day: "numeric", month: "short" });

  return (
    <div style={wrap} data-testid="glass-app">
      <style>{CSS}</style>
      {/* индикатор до перелистывания */}
      {n > 1 && cur.id !== "nav" && !(runActive && ["run", "voice", "debrief", "breath"].includes(cur.id)) && <div key={`timer-${idx}`} style={{ position: "absolute", top: 0, left: 0, height: 3, background: C.green,
                                                    animation: `glassTimer ${ROTATE_MS}ms linear`, opacity: 0.55 }} data-testid="glass-timer" />}
      {/* header */}
      <div style={{ ...row, justifyContent: "space-between", paddingBottom: 11, borderBottom: `1px solid ${C.faint}`, marginBottom: 13 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.green, letterSpacing: 0.5 }}>LifeOS</div>
          <div style={{ fontSize: 14, color: C.dim, textTransform: "capitalize" }}>{dateLabel}</div>
        </div>
        <span style={{ fontSize: 46, fontWeight: 900, letterSpacing: 1, lineHeight: 1, ...NUM }} data-testid="glass-clock">{clock}</span>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{data.name}</div>
          <div style={{ fontSize: 13, color: recColor(h?.recovery), ...NUM }}>{h?.recovery != null ? `⬤ ${Math.round(h.recovery)}%` : ""}</div>
        </div>
      </div>

      <div key={cur.id} style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, animation: "glassFade .45s ease" }}>
        {cur.id === "today" && <TodayScreen data={data} glassKey={key} />}
        {cur.id === "briefing" && <BriefingScreen text={data.briefing} name={data.name} />}
        {cur.id === "health" && <HealthScreen h={h} />}
        {cur.id === "pulse" && <PulseScreen glassKey={key} />}
        {cur.id === "workout" && <WorkoutScreen w={data.workout} />}
        {cur.id === "nutrition" && <NutritionScreen n={data.nutrition} />}
        {cur.id === "family" && <FamilyScreen fam={data.family} />}
        {cur.id === "radar" && <RadarScreen glassKey={key} />}
        {cur.id === "weather" && <WeatherScreen w={data.weather} />}
        {cur.id === "nav" && <NavScreen glassKey={key} />}
        {cur.id === "run" && <RunScreen glassKey={key} onActive={setRunActive} />}
        {cur.id === "voice" && <VoiceScreen glassKey={key} onActive={setRunActive} />}
        {cur.id === "breath" && <BreathScreen onActive={setRunActive} />}
        {cur.id === "eta" && <EtaScreen glassKey={key} />}
        {cur.id === "debrief" && <DebriefScreen glassKey={key} onActive={setRunActive} />}
        {cur.id === "evening" && <EveningScreen data={data} />}
      </div>

      {/* footer */}
      <div style={{ ...row, justifyContent: "center", gap: 10, marginTop: 8 }}>
        <span style={{ fontSize: 20, color: C.faint }}>‹</span>
        {screens.map((s, i) => (
          <span key={s.id} style={{ width: 8, height: 8, borderRadius: "50%", background: i === idx ? C.green : "#2a2f36" }} />
        ))}
        <span style={{ fontSize: 20, color: C.faint }}>›</span>
        <span style={{ fontSize: 12, letterSpacing: 2, fontWeight: 800, color: C.green, marginLeft: 6 }}>{cur.name}</span>
      </div>
    </div>
  );
}
